import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [passwordsConfigured, setPasswordsConfigured] = useState(null);

  // Check auth status on mount
  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Set up admin session timeout check
  useEffect(() => {
    if (user?.role === "admin" && user?.expires_at) {
      const checkExpiry = () => {
        const now = new Date();
        const expiresAt = new Date(user.expires_at);
        if (now > expiresAt) {
          logout();
        }
      };

      // Check every 30 seconds
      const interval = setInterval(checkExpiry, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const checkAuthStatus = async () => {
    try {
      // First check if passwords are configured
      const statusRes = await axios.get(`${API}/auth/status`);
      setPasswordsConfigured(statusRes.data.passwords_configured);

      // Then check if we have a stored token
      const token = localStorage.getItem("auth_token");
      if (token) {
        const res = await axios.get(`${API}/auth/verify`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (res.data.authenticated) {
          // Check if admin session expired
          if (res.data.expires_at) {
            const now = new Date();
            const expiresAt = new Date(res.data.expires_at);
            if (now > expiresAt) {
              localStorage.removeItem("auth_token");
              setUser(null);
            } else {
              setUser({ role: res.data.role, expires_at: res.data.expires_at, token });
            }
          } else {
            setUser({ role: res.data.role, token });
          }
        }
      }
    } catch (error) {
      localStorage.removeItem("auth_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (password) => {
    const res = await axios.post(`${API}/auth/login`, { password });
    const { token, role, expires_at } = res.data;
    
    localStorage.setItem("auth_token", token);
    setUser({ role, expires_at, token });
    
    return res.data;
  };

  const setupPasswords = async (adminPassword, posPassword) => {
    await axios.post(`${API}/auth/setup`, {
      admin_password: adminPassword,
      pos_password: posPassword
    });
    setPasswordsConfigured(true);
  };

  const logout = useCallback(async () => {
    try {
      const token = localStorage.getItem("auth_token");
      if (token) {
        await axios.post(`${API}/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (error) {
      // Ignore logout errors
    } finally {
      localStorage.removeItem("auth_token");
      setUser(null);
    }
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("auth_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const isAdmin = user?.role === "admin";
  const isPOS = user?.role === "pos";
  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      passwordsConfigured,
      login,
      logout,
      setupPasswords,
      getAuthHeaders,
      isAdmin,
      isPOS,
      isAuthenticated,
      checkAuthStatus
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
